import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import CommonToolbar from './CommonToolbar';
import * as securitySlice from '../../../data/security.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import securityUtils from '../../../services/securityUtils';
import PermissionDataTable from './PermissionDataTable';
import PermissionDialog from './PermissionDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import {
  Permission,
  PermissionResource,
  PermissionAction,
  PermissionScope,
} from '../../../data/models/permission.model';
import CommonToolbarWithSearchBar from './CommonToolbarWithSearchBar';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'Permissions';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(() => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
}));

enum PermissionExportColumn {
  Name = 'Permission Name',
  Description = 'Permission Description',
  Action = 'Action',
  Scope = 'Scope',
  Resource = 'Resource',
  Active = 'Active',
}

interface PermissionExportRow {
  [PermissionExportColumn.Name]: string;
  [PermissionExportColumn.Description]: string;
  [PermissionExportColumn.Action]: string;
  [PermissionExportColumn.Scope]: string;
  [PermissionExportColumn.Resource]: string;
  [PermissionExportColumn.Active]: string;
}

function mapPermissionToExport(
  permission: Permission,
  resources: PermissionResource[],
  actions: PermissionAction[],
  scopes: PermissionScope[],
): PermissionExportRow {
  const name = securityUtils.getPermissionName(
    permission,
    resources,
    actions,
    scopes,
  );

  return {
    [PermissionExportColumn.Name]: name,
    [PermissionExportColumn.Description]: permission.description ?? '',
    [PermissionExportColumn.Action]:
      actions.find(
        (a) => a.permissionActionID === permission.permissionActionID,
      )?.permissionActionName ?? '',
    [PermissionExportColumn.Scope]:
      scopes.find((s) => s.permissionScopeID === permission.permissionScopeID)
        ?.permissionScopeName ?? '',
    [PermissionExportColumn.Resource]:
      resources.find(
        (r) => r.permissionResourceID === permission.permissionResourceID,
      )?.permissionResourceName ?? '',
    [PermissionExportColumn.Active]:
      permission.userPermissionState === 'active' ? 'True' : 'False',
  };
}

export default function Permissions() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const readOnly = false;
  const isSuperReaderUser = useIsSuperReaderUser();

  const loadingUserPermissions = useAppSelector(
    securitySlice.selectLoadingUserPermissions,
  );
  const userPermissions = useAppSelector(securitySlice.selectUserPermissions);
  const resources = useAppSelector(lookupSlice.selectPermissionResources);
  const actions = useAppSelector(lookupSlice.selectPermissionActions);
  const scopes = useAppSelector(lookupSlice.selectPermissionScopes);
  const loadingAllPermissions = useAppSelector(
    securitySlice.selectLoadingAllPermissions,
  );

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [confirmDeleteMessage, setConfirmDeleteMessage] = React.useState('');
  const [permissionToDelete, setPermissionToDelete] =
    React.useState<Permission>();

  const [permission, setPermission] = React.useState<Permission>();
  const [searchText, setSearchText] = React.useState('');
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'description', sort: 'asc' },
  ]);

  React.useEffect(() => {
    const sort = sortModel[0]
      ? {
          column: sortModel[0].field,
          direction: sortModel[0].sort as 'asc' | 'desc',
        }
      : null;
    dispatch(securitySlice.fetchUserPermissionsAsync(searchText, sort));
    dispatch(lookupSlice.fetchLookupItemsAsync('permissionResource'));
    dispatch(lookupSlice.fetchLookupItemsAsync('permissionAction'));
    dispatch(lookupSlice.fetchLookupItemsAsync('permissionScope'));
  }, [dispatch, searchText, sortModel]);

  const confirmAddPermission = () => {
    setConfirmOpen(true);
  };

  const cancelAddPermission = () => {
    setConfirmOpen(false);
  };

  const addPermission = () => {
    setConfirmOpen(false);
    setPermission(undefined);
    setEditorOpen(true);
  };

  const editPermission = (id: number) => {
    const permission = userPermissions.find((g) => g.userPermissionID === id);
    if (permission) {
      setPermission(permission);
      setEditorOpen(true);
    }
  };

  const deletePermission = (id: number) => {
    const permission = userPermissions.find((g) => g.userPermissionID === id);
    if (permission) {
      const name = securityUtils.getPermissionName(
        permission,
        resources,
        actions,
        scopes,
      );

      const msg = t('permissions.confirmDeleteMessage', { name });
      setConfirmDeleteMessage(msg);
      setPermissionToDelete(permission);
      setConfirmDialogOpen(true);
    }
  };

  const confirmDelete = () => {
    if (permissionToDelete) {
      dispatch(
        securitySlice.deleteUserPermissionAsync(
          permissionToDelete.userPermissionID!,
        ),
      );
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const closePermissionEditor = (saved: boolean) => {
    setEditorOpen(false);
    setPermission(undefined);
  };

  const onSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const exportDisplayedPermissions = userPermissions.map((permission) =>
    mapPermissionToExport(permission, resources, actions, scopes),
  );

  const sortModelMemo = React.useMemo(() => {
    const sort = sortModel[0];
    const direction: 'asc' | 'desc' =
      sort && ['asc', 'desc'].includes(sort.sort as string)
        ? (sort.sort as 'asc' | 'desc')
        : 'asc';
    return sort ? { column: sort.field, direction } : null;
  }, [sortModel]);

  const fetchAllPermissions = useCallback(async () => {
    const result = await dispatch(
      securitySlice.fetchAllPermissionsForExportAsync('', sortModelMemo),
    );
    return (result ?? []).map((permission: Permission) =>
      mapPermissionToExport(permission, resources, actions, scopes),
    );
  }, [dispatch, sortModelMemo, resources, actions, scopes]);

  return (
    <StyledBox className={classes.container}>
      <CommonToolbarWithSearchBar
        label={t('permissions.search')}
        placeholder={t('permissions.searchPlaceholder')}
        button={readOnly || isSuperReaderUser ? null : t('permissions.add')}
        onCreate={confirmAddPermission}
        onSearchTextChange={onSearchTextChange}
        searchText={searchText}
        exportDataMap={{
          [t('permissions.displayedPermissions')]: exportDisplayedPermissions,
          [t('permissions.allPermissions')]: fetchAllPermissions,
        }}
        exportFilenamePrefixMap={{
          [t('permissions.displayedPermissions')]: t(
            'permissions.displayedPermissions',
          ),
          [t('permissions.allPermissions')]: t('permissions.allPermissions'),
        }}
        disabled={loadingAllPermissions}
      />
      <PermissionDataTable
        permissions={userPermissions}
        resources={resources}
        actions={actions}
        scopes={scopes}
        loading={loadingUserPermissions}
        readOnly={readOnly}
        onEdit={editPermission}
        onDelete={deletePermission}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
      />
      <PermissionDialog
        open={editorOpen}
        permission={permission}
        resources={resources}
        actions={actions}
        scopes={scopes}
        close={closePermissionEditor}
      />
      <ConfirmationDialog
        title={t('permissions.confirmAddTitle')}
        message={t('permissions.confirmAddMessage')}
        open={confirmOpen}
        positiveAction={addPermission}
        negativeAction={cancelAddPermission}
      />
      <ConfirmationDialog
        title={t('permissions.deletePermission')}
        message={confirmDeleteMessage}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
    </StyledBox>
  );
}
